import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  selectSubtitlesEnabled,
  selectCurrentSubtitle,
  selectIsFinal,
  selectIsTranscribing,
  selectInterpreterName,
  clearSubtitle,
} from '../../features/subtitles/subtitlesSlice';
import './SubtitleOverlay.css';

/** How long a final subtitle stays on screen with no follow-up */
const SUBTITLE_LINGER_MS = 5000;

export interface SubtitleOverlayProps {
  /** Position of the subtitle overlay */
  position?: 'bottom' | 'top';
  /** Maximum width of the subtitle container (CSS value) */
  maxWidth?: string;
  /** Additional CSS class name */
  className?: string;
}

/**
 * SubtitleOverlay Component
 *
 * Displays real-time subtitles from interpreter speech recognition.
 * Intended to be overlaid on the video player.
 */
const SubtitleOverlay: React.FC<SubtitleOverlayProps> = ({
  position = 'bottom',
  maxWidth = '80%',
  className = '',
}) => {
  const enabled = useAppSelector(selectSubtitlesEnabled);
  const currentText = useAppSelector(selectCurrentSubtitle);
  const isFinal = useAppSelector(selectIsFinal);
  const isTranscribing = useAppSelector(selectIsTranscribing);
  const interpreterName = useAppSelector(selectInterpreterName);
  const dispatch = useAppDispatch();

  // A final subtitle with no follow-up would otherwise stay on screen for
  // the rest of the call — clear it after a linger period
  useEffect(() => {
    if (!isFinal || !currentText) {
      return;
    }
    const timer = setTimeout(() => dispatch(clearSubtitle()), SUBTITLE_LINGER_MS);
    return () => clearTimeout(timer);
  }, [isFinal, currentText, dispatch]);

  // Don't render if subtitles are disabled or there's no text
  if (!enabled || (!currentText && !isTranscribing)) {
    return null;
  }

  const positionClass = position === 'top' ? 'subtitle-overlay-top' : 'subtitle-overlay-bottom';
  const textClass = isFinal ? 'subtitle-text-final' : 'subtitle-text-interim';

  return (
    <div
      className={`subtitle-overlay ${positionClass} ${className}`}
      style={{ maxWidth }}
      role="region"
      aria-label="Live subtitles"
      aria-live="polite"
      aria-atomic="false"
    >
      <div className="subtitle-container">
        {interpreterName && (
          <span className="subtitle-speaker">{interpreterName}: </span>
        )}
        <span className={`subtitle-text ${textClass}`}>
          {currentText}
          {isTranscribing && !isFinal && <span className="subtitle-cursor" aria-hidden="true">|</span>}
        </span>
      </div>
    </div>
  );
};

export default SubtitleOverlay;
